import React, { useLayoutEffect, useRef, useState, useMemo } from "react";

import {
  Root,
  color,
  Circle,
  Container,
  Bullet,
  Tooltip,
} from "@amcharts/amcharts5";
import {
  MapChart,
  MapPointSeries,
  MapPolygonSeries,
  ZoomControl,
} from "@amcharts/amcharts5/map";
import * as am5map from "@amcharts/amcharts5/map";
import am5themes_Animated from "@amcharts/amcharts5/themes/Animated";
import am5geodata_worldLow from "@amcharts/amcharts5-geodata/worldLow";
import { X } from "lucide-react";

import { regionOptions as regionOptionsFromConstants } from "../../Utils/Constants/Constants";
import { useAccountTiers } from "../../Utils/Helper/accountTierHelpers";
import { escapeHtml } from "../../Utils/Helper/escapeHtml";
import { getAccountCountsByStatus } from "../../Utils/Helper/WorldMapHelpers";
import Select from "../Select";
import "./style.scss";

// Account data interface for WorldMap
export interface WorldMapAccount {
  name: string;
  status: "healthy" | "at-risk" | "critical" | "unassigned" | string;
  location?: string;
  latitude?: number | null;
  longitude?: number | null;
  revenue?: string | number;
  csat?: string | number;
  tier?: string;
  sponsor?: string;
  lastTouch?: string;
}

interface WorldMapProps {
  accounts: WorldMapAccount[];
  title?: string;
  size?: "sm" | "md" | "lg" | "xl";
  showTitle?: boolean;
  className?: string;
  showFullViewButton?: boolean;
  onFullViewClick?: () => void;
  onNavigateToAccounts?: () => void;
  allAccounts?: WorldMapAccount[]; // All accounts for filtering (if different from displayed accounts)
  disableFullView?: boolean; // Prevent full view modal when already in modal
  showInlineLegend?: boolean; // Show legend (with counts when available)
}

const WorldMap: React.FC<WorldMapProps> = ({
  accounts,
  title = "Global Accounts",
  size = "lg",
  showTitle = true,
  className = "",
  showFullViewButton = false,
  onFullViewClick,
  onNavigateToAccounts,
  allAccounts,
  disableFullView = false,
  showInlineLegend = true,
}) => {
  const chartRef = useRef<HTMLDivElement>(null);
  const rootRef = useRef<Root | null>(null);
  const { tierOptions: dynamicTierOptions, formatTier } = useAccountTiers();

  // Full view modal state
  const [showFullView, setShowFullView] = useState(false);
  const [filters, setFilters] = useState({
    score: "All Score",
    tier: "All Tiers",
    region: "All Regions",
  });
  const [hoveredAccount, setHoveredAccount] = useState<WorldMapAccount | null>(
    null
  );
  const hoveredAccountPosition = {
    x: 0,
    y: 0,
  };

  // Use allAccounts if provided, otherwise use accounts
  const allAccountsData = allAccounts || accounts;

  // Filter accounts based on filters
  const filteredAccounts = useMemo((): WorldMapAccount[] => {
    return allAccountsData.filter(account => {
      // Score filter: High=healthy(low), Medium=at-risk(medium), Low=critical(high)
      if (filters.score !== "All Score") {
        const status = (account.status || "").toLowerCase();
        const scoreToStatus: Record<string, string> = {
          "High Score": "healthy",
          "Medium Score": "at-risk",
          "Low Score": "critical",
        };
        const expectedStatus = scoreToStatus[filters.score];
        if (expectedStatus && status !== expectedStatus) {
          return false;
        }
      }

      // Region filter
      if (filters.region !== "All Regions") {
        const accountRegion = account.location?.toUpperCase() || "";
        const filterRegion = filters.region.toUpperCase();

        // Map region names
        const regionMap: Record<string, string[]> = {
          APAC: [
            "ASIA",
            "ASIA PACIFIC",
            "APAC",
            "AUSTRALIA",
            "SINGAPORE",
            "JAPAN",
            "CHINA",
            "INDIA",
          ],
          AMERICAS: [
            "AMERICA",
            "AMERICAS",
            "USA",
            "US",
            "UNITED STATES",
            "CANADA",
            "BRAZIL",
            "MEXICO",
          ],
          EMEA: [
            "EMEA",
            "EUROPE",
            "EU",
            "UK",
            "UNITED KINGDOM",
            "GERMANY",
            "FRANCE",
            "SPAIN",
            "ITALY",
            "NETHERLANDS",
          ],
        };

        const regions = regionMap[filterRegion] || [];
        if (!regions.some(r => accountRegion.includes(r))) {
          return false;
        }
      }

      // Tier filter
      if (filters.tier !== "All Tiers") {
        const accountTier = account.tier?.toUpperCase() || "";
        const filterTier = filters.tier.toUpperCase();
        if (accountTier !== filterTier) {
          return false;
        }
      }

      return true;
    });
  }, [allAccountsData, filters]);

  // Get accounts to display (use filtered if in full view, otherwise use provided accounts)
  // Only use filtered accounts when in full view modal, otherwise use the provided accounts
  const accountsToDisplay =
    showFullView && !disableFullView ? filteredAccounts : accounts;

  // Filter options
  const scoreOptions = [
    { label: "All Score", value: "All Score" },
    { label: "High Score", value: "High Score" },
    { label: "Medium Score", value: "Medium Score" },
    { label: "Low Score", value: "Low Score" },
  ];

  const regionOptions = [
    { label: "All Regions", value: "All Regions" },
    ...regionOptionsFromConstants,
  ];

  const tierOptions = useMemo(
    () => [
      { label: "All Tiers", value: "All Tiers" },
      ...dynamicTierOptions.map(option => ({
        label: option.label,
        value: option.value,
      })),
    ],
    [dynamicTierOptions]
  );

  useLayoutEffect(() => {
    if (!chartRef.current) return;

    // Dispose of existing chart if it exists
    if (rootRef.current) {
      rootRef.current.dispose();
      rootRef.current = null;
    }

    // Create root element
    const root = Root.new(chartRef.current);
    rootRef.current = root;

    // Set themes
    root.setThemes([am5themes_Animated.new(root)]);

    // Create the map chart
    const chart = root.container.children.push(
      MapChart.new(root, {
        panX: "translateX",
        panY: "translateY",
        projection: am5map.geoMercator(),
        homeZoomLevel: 1,
        homeGeoPoint: { longitude: 0, latitude: 0 },
      })
    );

    // Create world polygon series
    const worldSeries = chart.series.push(
      MapPolygonSeries.new(root, {
        geoJSON: am5geodata_worldLow,
        exclude: ["AQ"], // Exclude Antarctica
      })
    );

    // Configure world polygons
    worldSeries.mapPolygons.template.setAll({
      fill: color("#e5e7eb"),
      stroke: color("#d1d5db"),
      strokeWidth: 0.5,
      tooltipText: "{name}",
    });

    // Create point series for accounts
    const pointSeries = chart.series.push(
      MapPointSeries.new(root, {
        valueField: "value",
        calculateAggregates: true,
      })
    );

    // Helper function to get coordinates from backend-provided lat/lng
    const getCoordinatesFromLocation = (
      location?: string,
      dbLatitude?: number | null,
      dbLongitude?: number | null
    ) => {
      if (dbLatitude && dbLongitude && dbLatitude !== 0 && dbLongitude !== 0) {
        return { longitude: dbLongitude, latitude: dbLatitude };
      }
      // No coordinates available, mark as invalid so it can be filtered out
      return { longitude: 0, latitude: 0 };
    };

    // Helper function to convert status to AmCharts color
    const getStatusColorForAmCharts = (status?: string) => {
      switch (status?.toLowerCase()) {
        case "healthy":
          return color("#22c55e");
        case "at risk":
        case "at-risk":
          return color("#f59e0b");
        case "critical":
          return color("#ef4444");
        case "unassigned":
          return color("#6b7280");
        default:
          return color("#8b5cf6");
      }
    };

    // Convert accounts to AmCharts format and filter out accounts with (0,0) coordinates
    const chartData =
      accountsToDisplay
        ?.map(account => {
          // Try to get coordinates from database first, then fallback to location string
          const coords = getCoordinatesFromLocation(
            account.location,
            account.latitude,
            account.longitude
          );
          const fillColor = getStatusColorForAmCharts(account.status);
          const displayName = account.name || "Unknown Company";
          const initials = displayName
            .split(" ")
            .map((word: string) => word.charAt(0))
            .join("")
            .substring(0, 2)
            .toUpperCase();

          const statusKey = (account.status || "").toLowerCase();
          let statusTextColor = "#6b7280";
          let statusBgColor = "#f9fafb";
          switch (statusKey) {
            case "critical":
              statusTextColor = "#ef4444";
              statusBgColor = "#fef2f2";
              break;
            case "at risk":
            case "at-risk":
              statusTextColor = "#f59e0b";
              statusBgColor = "#fffbeb";
              break;
            case "healthy":
              statusTextColor = "#22c55e";
              statusBgColor = "#f0fdf4";
              break;
            default:
              break;
          }

          const sponsorLabel = account.sponsor || "Unassigned";
          const sponsorColor =
            sponsorLabel.toLowerCase() === "unassigned" ? "#ef4444" : "#111827";

          return {
            geometry: {
              type: "Point",
              coordinates: [coords.longitude, coords.latitude],
            },
            name: escapeHtml(displayName),
            location: escapeHtml(account.location || "Unknown Location"),
            status: escapeHtml(account.status || "Unknown"),
            revenue: escapeHtml(String(account.revenue ?? "N/A")),
            csat: escapeHtml(String(account.csat ?? "N/A")),
            lastTouch: escapeHtml(String(account.lastTouch ?? "N/A")),
            tier: escapeHtml(
              formatTier(account.tier) || account.tier || "Unknown"
            ),
            sponsor: escapeHtml(sponsorLabel),
            initials: escapeHtml(initials),
            statusColor: statusTextColor,
            statusBgColor,
            sponsorColor,
            value: 1,
            fill: fillColor,
            // Include raw coordinates for filtering
            rawLatitude: coords.latitude,
            rawLongitude: coords.longitude,
          };
        })
        .filter(item => {
          // Filter out invalid (0,0) coordinates (center of Atlantic Ocean)
          const isValid = !(item.rawLatitude === 0 && item.rawLongitude === 0);
          return isValid;
        }) || [];

    // Add jitter to overlapping coordinates so all dots are visible
    const coordinateGroups: Record<string, number[]> = {};
    chartData.forEach((item, index) => {
      const coordKey = `${item.rawLatitude.toFixed(4)},${item.rawLongitude.toFixed(4)}`;
      if (!coordinateGroups[coordKey]) {
        coordinateGroups[coordKey] = [];
      }
      coordinateGroups[coordKey].push(index);
    });

    // Apply small random offset to accounts with duplicate coordinates
    Object.entries(coordinateGroups).forEach(([coordKey, indices]) => {
      if (indices.length > 1) {
        // Multiple accounts at same location - add jitter
        indices.forEach((position, idx) => {
          const index = indices[idx];
          const item = chartData[index];
          // Calculate offset based on position in the group
          // Use a small offset (approximately 0.1-0.5 degrees, which is about 10-50km)
          const angle = (2 * Math.PI * idx) / indices.length; // Distribute in a circle
          const radius = 0.15; // ~15km offset
          const latOffset = radius * Math.cos(angle);
          const lngOffset = radius * Math.sin(angle);

          // Update coordinates with offset
          item.geometry.coordinates = [
            item.rawLongitude + lngOffset,
            item.rawLatitude + latOffset,
          ];
        });
      }
    });

    // Configure point bullets with status-based colors
    pointSeries.bullets.push((root, series, dataItem) => {
      const container = Container.new(root, {});
      const dataContext = dataItem.dataContext as any;

      const circle = container.children.push(
        Circle.new(root, {
          radius: 8,
          fill: dataContext?.fill || color("#8b5cf6"),
          stroke: color("#ffffff"),
          strokeWidth: 2,
          tooltipText: "{name}",
          cursorOverStyle: "pointer",
        })
      );

      // Add hover effects
      circle.states.create("hover", {
        scale: 1.3,
      });

      return Bullet.new(root, {
        sprite: container,
      });
    });

    // Customize tooltip appearance
    const tooltip = Tooltip.new(root, {
      pointerOrientation: "vertical",
      paddingTop: 16,
      paddingBottom: 16,
      paddingLeft: 16,
      paddingRight: 16,
      getFillFromSprite: false,
    });

    pointSeries.set("tooltip", tooltip);

    // Custom tooltip content formatter - set background styling
    const tooltipBackground = tooltip.get("background");
    if (tooltipBackground) {
      tooltipBackground.setAll({
        fill: color("#ffffff"),
        stroke: color("#e5e7eb"),
        strokeWidth: 1,
      });
    }

    // Set custom HTML tooltip content (colors/initials come from data fields)
    tooltip.set(
      "labelHTML",
      `
      <div style="font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif; max-width: 280px;">
        <div style="display: flex; align-items: center; margin-bottom: 12px;">
          <div style="display: flex; width: 32px; height: 32px; flex-shrink: 0; align-items: center; justify-content: center; margin-right: 12px; border-radius: 9999px; background: #dbeafe;">
            <span style="font-size: 12px; font-weight: 700; color: #2563eb; line-height: 1;">{initials}</span>
          </div>
          <div style="flex: 1; min-width: 0;">
            <div style="font-weight: 600; font-size: 16px; color: #111827; margin-bottom: 2px;">{name}</div>
            <div style="font-size: 14px; color: #6b7280;">{location}</div>
          </div>
        </div>
        
        <div style="margin-bottom: 12px;">
          <span style="font-size: 14px; color: {statusColor}; font-weight: 500; padding: 4px 8px; background: {statusBgColor}; border-radius: 4px; display: inline-block;">{status}</span>
        </div>
        
        <div style="display: grid; grid-template-columns: 1fr 1fr; gap: 12px; margin-top: 12px;">
          <div>
            <div style="font-size: 12px; color: #9ca3af; margin-bottom: 2px;">Revenue</div>
            <div style="font-size: 14px; color: #111827; font-weight: 500;">{revenue}</div>
          </div>
          <div>
            <div style="font-size: 12px; color: #9ca3af; margin-bottom: 2px;">Tier</div>
            <div style="font-size: 14px; color: #111827; font-weight: 500;">{tier}</div>
          </div>
        </div>
        
        <div style="margin-top: 12px; padding-top: 12px; border-top: 1px solid #f3f4f6;">
          <div style="font-size: 12px; color: #9ca3af; margin-bottom: 2px;">Sponsor</div>
          <div style="font-size: 14px; color: {sponsorColor}; font-weight: 500;">{sponsor}</div>
        </div>
      </div>
    `
    );

    // Set data
    pointSeries.data.setAll(chartData);

    // Add zoom controls
    const zoomControl = ZoomControl.new(root, {
      paddingTop: 20,
      paddingRight: 20,
      opacity: 1,
      interactiveChildren: true,
    });

    zoomControl.set("forceHidden", false);
    zoomControl.set("visible", true);

    chart.set("zoomControl", zoomControl);

    // Make chart responsive
    root.interfaceColors.set("alternativeText", color("#000000"));

    return () => {
      if (rootRef.current) {
        rootRef.current.dispose();
        rootRef.current = null;
      }
    };
  }, [accountsToDisplay, size, formatTier]);

  // Calculate height based on size prop
  const getHeight = () => {
    switch (size) {
      case "sm":
        return "h-64";
      case "md":
        return "h-80";
      case "lg":
        return "h-96";
      case "xl":
        return "h-[600px]";
      default:
        return "h-96";
    }
  };

  // Get account counts for legend
  const accountCounts = useMemo(() => {
    return getAccountCountsByStatus(allAccountsData);
  }, [allAccountsData]);

  // Full View Modal Component
  const FullViewModal = () => {
    if (!showFullView) return null;

    const handleBackdropClick = (e: React.MouseEvent<HTMLDivElement>) => {
      if (e.target === e.currentTarget) {
        setFilters({
          score: "All Score",
          tier: "All Tiers",
          region: "All Regions",
        });
        setShowFullView(false);
      }
    };

    const handleCloseClick = (e: React.MouseEvent<HTMLButtonElement>) => {
      e.preventDefault();
      e.stopPropagation();
      setFilters({
        score: "All Score",
        tier: "All Tiers",
        region: "All Regions",
      });
      setShowFullView(false);
      setHoveredAccount(null);
    };

    return (
      <div
        className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4"
        style={{ zIndex: 10000 }}
        onClick={handleBackdropClick}
      >
        <div
          className="bg-white rounded-xl w-full max-w-7xl h-5/6 flex flex-col shadow-2xl"
          onClick={e => e.stopPropagation()}
        >
          {/* Header */}
          <div className="flex items-center justify-between p-6 border-b border-gray-200">
            <h2 className="text-xl font-bold text-gray-900">
              Command Center - Accounts Full View
            </h2>
            <div className="flex items-center space-x-4">
              {onNavigateToAccounts && (
                <button
                  onClick={() => {
                    setShowFullView(false);
                    onNavigateToAccounts();
                  }}
                  className="text-purple-600 text-sm hover:text-purple-700 font-medium"
                >
                  See All
                </button>
              )}
              <button
                onClick={handleCloseClick}
                className="text-gray-400 hover:text-gray-600 p-2 hover:bg-gray-100 rounded-full transition-colors relative z-50"
                type="button"
                aria-label="Close modal"
                title="Close modal"
              >
                <X className="h-6 w-6" />
              </button>
            </div>
          </div>

          {/* Map Container */}
          <div className="flex-1 bg-gray-100 relative overflow-hidden">
            {/* Map Header */}
            <div
              className="absolute top-6 left-6 right-6"
              style={{ zIndex: 1 }}
            >
              <div className="bg-white rounded-lg p-4 shadow-lg border border-gray-200">
                <div className="flex items-center justify-between">
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900">
                      Our Customers Map view
                    </h3>
                    <p className="text-sm text-gray-500">
                      Showing {filteredAccounts.length} of{" "}
                      {allAccountsData.length} accounts
                    </p>
                  </div>
                  <div
                    className="flex items-center space-x-4"
                    onClick={e => e.stopPropagation()}
                  >
                    <div className="flex items-center space-x-2">
                      <span className="text-sm text-gray-600">Score:</span>
                      <Select
                        name="score"
                        value={filters.score}
                        onChange={e => {
                          e.stopPropagation();
                          setFilters(prev => ({
                            ...prev,
                            score: e.target.value,
                          }));
                        }}
                        options={scoreOptions}
                        className="w-40"
                        isShowPlaceHolder={false}
                      />
                    </div>
                    <div className="flex items-center space-x-2">
                      <span className="text-sm text-gray-600">Region:</span>
                      <Select
                        name="region"
                        value={filters.region}
                        onChange={e => {
                          e.stopPropagation();
                          setFilters(prev => ({
                            ...prev,
                            region: e.target.value,
                          }));
                        }}
                        options={regionOptions}
                        className="w-40"
                        isShowPlaceHolder={false}
                      />
                    </div>
                    <div className="flex items-center space-x-2">
                      <span className="text-sm text-gray-600">Tier:</span>
                      <Select
                        name="tier"
                        value={filters.tier}
                        onChange={e => {
                          e.stopPropagation();
                          setFilters(prev => ({
                            ...prev,
                            tier: e.target.value,
                          }));
                        }}
                        options={tierOptions}
                        className="w-32"
                        isShowPlaceHolder={false}
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Professional World Map */}
            <div
              className="absolute inset-0 mt-20"
              style={{ pointerEvents: "auto" }}
            >
              <div className="w-full h-full">
                <WorldMap
                  key={`fullview-${filters.score}-${filters.region}-${filters.tier}`}
                  accounts={filteredAccounts}
                  title="Global Account Distribution"
                  size="xl"
                  showTitle={false}
                  allAccounts={allAccountsData}
                  showFullViewButton={false}
                  disableFullView={true}
                />
              </div>
            </div>

            {/* Legend */}
            <div
              className="absolute bottom-6 left-6 bg-white  rounded-lg p-4 shadow-lg border border-gray-200"
              style={{ zIndex: 2 }}
            >
              <h4 className="text-sm font-semibold text-gray-900 mb-3">
                {accountCounts.total} Customer
                {accountCounts.total !== 1 ? "s" : ""} Globally
                {filteredAccounts.length !== accountCounts.total && (
                  <span className="text-xs text-gray-500 block">
                    ({filteredAccounts.length} shown)
                  </span>
                )}
              </h4>
              <div className="grid grid-cols-2 gap-3 text-sm">
                <div className="flex items-center">
                  <div className="w-3 h-3 bg-emerald-500 rounded-full mr-2"></div>
                  <span className="text-gray-600">
                    Healthy ·{" "}
                    {accountCounts.healthy.toString().padStart(2, "0")}
                  </span>
                </div>
                <div className="flex items-center">
                  <div className="w-3 h-3 bg-amber-500 rounded-full mr-2"></div>
                  <span className="text-gray-600">
                    At Risk · {accountCounts.atRisk.toString().padStart(2, "0")}
                  </span>
                </div>
                <div className="flex items-center">
                  <div className="w-3 h-3 bg-red-500 rounded-full mr-2"></div>
                  <span className="text-gray-600">
                    Critical ·{" "}
                    {accountCounts.critical.toString().padStart(2, "0")}
                  </span>
                </div>
                <div className="flex items-center">
                  <div className="w-3 h-3 bg-gray-500 rounded-full mr-2"></div>
                  <span className="text-gray-600">
                    Unassigned ·{" "}
                    {accountCounts.unassigned.toString().padStart(2, "0")}
                  </span>
                </div>
              </div>
            </div>

            {/* Tooltip */}
            {hoveredAccount && (
              <div
                className="fixed z-30 bg-white  rounded-lg shadow-xl border border-gray-200 p-4 pointer-events-none"
                style={{
                  left: hoveredAccountPosition.x + 20,
                  top: hoveredAccountPosition.y - 100,
                  minWidth: "250px",
                }}
              >
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center">
                    <div className="w-8 h-8 bg-gray-100 rounded-full flex items-center justify-center mr-2">
                      <span className="text-xs font-bold text-gray-600">
                        {hoveredAccount.name.substring(0, 2)}
                      </span>
                    </div>
                    <div>
                      <h4 className="font-semibold text-gray-900">
                        {hoveredAccount.name}
                      </h4>
                      <p className="text-xs text-gray-600">
                        {hoveredAccount.location}
                      </p>
                    </div>
                  </div>
                  <span
                    className={`px-2 py-1 rounded text-xs font-medium ${
                      hoveredAccount.status === "Critical"
                        ? "bg-red-100 text-red-800"
                        : hoveredAccount.status === "At Risk"
                          ? "bg-amber-100 text-amber-800"
                          : hoveredAccount.status === "Healthy"
                            ? "bg-emerald-100 text-emerald-800"
                            : "bg-gray-100 text-gray-800"
                    }`}
                  >
                    {hoveredAccount.status}
                  </span>
                </div>

                <div className="space-y-1 text-sm">
                  <div className="flex justify-between">
                    <span className="text-gray-600">Revenue:</span>
                    <span className="font-medium text-gray-900">
                      {hoveredAccount.revenue}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">CSAT:</span>
                    <span className="font-medium text-gray-900">
                      {hoveredAccount.csat}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Last touch:</span>
                    <span className="font-medium text-gray-900">
                      {hoveredAccount.lastTouch}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Tier:</span>
                    <span className="font-medium text-gray-900">
                      {formatTier(hoveredAccount.tier) ||
                        hoveredAccount.tier ||
                        "Unknown"}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Sponsor:</span>
                    <span
                      className={`font-medium ${hoveredAccount.sponsor === "Unassigned" ? "text-red-600" : "text-gray-900"}`}
                    >
                      {hoveredAccount.sponsor}
                    </span>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    );
  };

  return (
    <>
      {!disableFullView && showFullView && <FullViewModal />}
      <div className={`w-full h-full ${className}`}>
        {showTitle && (
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-gray-900">{title}</h3>
            {showFullViewButton && !disableFullView && (
              <button
                onClick={() => {
                  if (onFullViewClick) {
                    onFullViewClick();
                  } else {
                    setShowFullView(true);
                  }
                }}
                className="text-purple-600 text-sm hover:text-purple-700 font-medium"
              >
                Full View
              </button>
            )}
          </div>
        )}

        {/* AmCharts World Map Container */}
        <div
          ref={chartRef}
          className={`w-full ${getHeight()} bg-white  rounded-lg shadow-sm border border-gray-200 relative`}
          style={{ minHeight: "300px" }}
        >
          {!showTitle && showFullViewButton && !disableFullView && (
            <button
              onClick={() => {
                setShowFullView(true);
                if (onFullViewClick) {
                  onFullViewClick();
                }
              }}
              className="full-view-btn absolute top-4 right-4 text-purple-600 text-sm hover:text-purple-700 font-medium bg-white  px-3 py-1 rounded-lg shadow-sm border border-gray-200"
            >
              Full View
            </button>
          )}
        </div>

        {/* Legend with counts (if allAccountsData available) */}
        {showInlineLegend && (
          <div className="mt-4 flex justify-center">
            <div className="flex flex-wrap gap-4 text-sm">
              <div className="flex items-center gap-2">
                <div className="w-4 h-4 bg-emerald-500 rounded-full border-2 border-white shadow"></div>
                <span className="text-gray-700">
                  Healthy
                  {accountCounts
                    ? ` · ${accountCounts.healthy.toString().padStart(2, "0")}`
                    : ""}
                </span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-4 h-4 bg-amber-500 rounded-full border-2 border-white shadow"></div>
                <span className="text-gray-700">
                  At Risk
                  {accountCounts
                    ? ` · ${accountCounts.atRisk.toString().padStart(2, "0")}`
                    : ""}
                </span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-4 h-4 bg-red-500 rounded-full border-2 border-white shadow"></div>
                <span className="text-gray-700">
                  Critical
                  {accountCounts
                    ? ` · ${accountCounts.critical.toString().padStart(2, "0")}`
                    : ""}
                </span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-4 h-4 bg-gray-500 rounded-full border-2 border-white shadow"></div>
                <span className="text-gray-700">
                  Unassigned
                  {accountCounts
                    ? ` · ${accountCounts.unassigned.toString().padStart(2, "0")}`
                    : ""}
                </span>
              </div>
              {accountCounts && (
                <div className="flex items-center gap-2 text-gray-700">
                  <span className="font-semibold">
                    {accountCounts.total} Customer
                    {accountCounts.total !== 1 ? "s" : ""}
                  </span>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </>
  );
};

export default WorldMap;
